import { createApi } from '@reduxjs/toolkit/query/react';

import { getData } from '../../services/API';
import {
  employeeRoundHistoryUrl,
  getCensusReportUrl,
  getComplianceReportUrl,
  getEmpComplianceReportUrl,
  getEmployeeFreqUrl,
  getEmployeeRoundsReportUrl,
  getEmployeeRoundsUrl,
  getPatientRoundsReportUrl,
  getPatientRoundsUrl,
  patientHistorySearchUrl,
  patientRoundHistoryUrl
} from '../../services/API/endpoints';
import { processError, processResponse } from './utils/transformations';
import { date, employeeHireDateHelper } from '../../utils/helpers';
import { setToastOptions, slideIn } from '../actions';
import qualifyingEmpRoundingTemplates from '../../constants/qualifyingEmpRoundingTemplates.json';

interface IGetByFacilityPayload {
  facilityId: string;
}

interface ISearchPatientHistoryPayload extends IGetByFacilityPayload {
  patientSearch: string;
}

interface IGetPatientRoundHistoryPayload extends IGetByFacilityPayload {
  patientId: string;
}

interface IGetEmployeeRoundHistoryPayload {
  employeeId: string;
  roundingTypes?: string[];
}

interface IGetComplianceReportPayload extends IGetByFacilityPayload {
  startDay: string;
  endDay: string;
}

interface IGetPatientRoundsReportDownloadPayload {
  facilityId: string;
  unitId: string[];
  query: string;
  roundedBy: string | string[];
  sortBy: string;
  sortOrder: string;
  startday: string;
  endday: string;
  isDownload?: boolean;
}

interface IGetPatientRoundsReportPayload
  extends IGetPatientRoundsReportDownloadPayload {
  page: number;
}

interface IGetCensusReportDownloadPayload {
  facilityId: string;
  unitId: string[];
  patient: string;
  user34s: string[];
  sortBy: string;
  sortOrder: string;
  startday: string;
  endday: string;
  status?: string;
  isDownload?: boolean;
}
interface IGetCensusReportPayload extends IGetCensusReportDownloadPayload {
  page: number;
  limit: number;
}

interface IGetEmployeeRoundsReportDownloadPayload {
  facilityId: string;
  query: string;
  roundedBy: string | string[];
  templateName: string;
  departments?: string;
  sortBy: string;
  sortOrder: string;
  startday: string;
  endday: string;
  isDownload?: boolean;
}
interface IGetEmployeeRoundsReportPayload
  extends IGetEmployeeRoundsReportDownloadPayload {
  page: number;
}

interface IGetEmployeeFreqReportDownloadPayload {
  facilityId: string;
  query: string;
  sortBy: string;
  sortOrder: string;
  startday: string;
  endday: string;
}
interface IGetEmployeeFreqReportPayload
  extends IGetEmployeeFreqReportDownloadPayload {
  page: number;
}

/**
 * RTK Query API slice for report features.
 *
 * @remarks
 * Provides endpoints for:
 * - Patient Rounds Report (`getPatientRoundsReport`)
 * - Employee Rounds Report (`getEmployeeRoundsReport`)
 * - Other report-related endpoints as defined
 *
 * @example
 * import { useGetPatientRoundsReportQuery } from 'src/redux/apis/Reports.api';
 * const { data } = useGetPatientRoundsReportQuery();
 */
export const reportsApi = createApi({
  reducerPath: 'reportsApi',
  baseQuery: (operation: any) => operation,
  tagTypes: [
    'patient-inapp-report',
    'patient-compliance-report',
    'patient-rounds-report',
    'patient-census-report',
    'patient-round-history',
    'search-patient-round-history',
    'employee-inapp-report',
    'employee-compliance-report',
    'employee-rounds-report',
    'employee-frequency-report',
    'employee-round-history',
    'patient-rounds-report-export',
    'employee-rounds-report-export',
    'patient-census-report-export',
    'employee-frequency-report-export'
  ],
  invalidationBehavior: 'immediately',
  endpoints: builder => ({
    /**
     * Patient Reports
     */
    getPatientComplianceReport: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetComplianceReportPayload) =>
        getData(
          `${getComplianceReportUrl}?facilityId=${payload.facilityId}&startDay=${payload.startDay}&endDay=${payload.endDay}`,
          {}
        ),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'patient-compliance-report',
          id: payload.facilityId
        }
      ]
    }),
    getPatientRoundsReport: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetPatientRoundsReportPayload) => {
        const params = new URLSearchParams(payload as any).toString();
        return getData(`${getPatientRoundsUrl}?${params}`, {});
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'patient-rounds-report',
          id: payload.facilityId
        }
      ]
    }),
    getCensusReport: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetCensusReportPayload) => {
        const params = new URLSearchParams(payload as any).toString();
        return getData(`${getCensusReportUrl}?${params}`, {});
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'patient-census-report',
          id: payload.facilityId
        }
      ]
    }),
    getPatientRoundHistory: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetPatientRoundHistoryPayload) => {
        return getData(
          `${patientRoundHistoryUrl}?facilityId=${payload.facilityId}&patientId=${payload.patientId}`,
          {}
        );
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'patient-round-history',
          id: payload.patientId
        }
      ]
    }),
    getSearchPatientRoundHistory: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: ISearchPatientHistoryPayload) => {
        let url;
        if (payload.patientSearch.match(/\d+/g)) {
          url = `${patientHistorySearchUrl}?facilityId=${payload.facilityId}&mrn=${payload.patientSearch}&page=-1`;
        } else {
          url = `${patientHistorySearchUrl}?facilityId=${payload.facilityId}&name=${payload.patientSearch}&page=-1`;
        }
        return getData(url, {});
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'search-patient-round-history',
          id: payload.patientSearch
        }
      ]
    }),
    /**
     * Employee Reports
     */
    getEmployeeComplianceReport: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetComplianceReportPayload) =>
        getData(
          `${getEmpComplianceReportUrl}?facilityId=${payload.facilityId}&startDay=${payload.startDay}&endDay=${payload.endDay}`,
          {}
        ),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'employee-compliance-report',
          id: payload.facilityId
        }
      ]
    }),
    getEmployeeRoundsReport: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetEmployeeRoundsReportPayload) => {
        const params = new URLSearchParams(payload as any).toString();
        return getData(`${getEmployeeRoundsUrl}?${params}`, {});
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'employee-rounds-report',
          id: payload.facilityId
        }
      ]
    }),
    getEmployeeFrequencyReport: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetEmployeeFreqReportPayload) => {
        const params = new URLSearchParams(payload as any).toString();
        return getData(`${getEmployeeFreqUrl}?${params}`, {});
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'employee-frequency-report',
          id: payload.facilityId
        }
      ]
    }),
    getEmployeeRoundHistory: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetEmployeeRoundHistoryPayload) => {
        return getData(
          `${employeeRoundHistoryUrl}?employeeId=${payload.employeeId}${
            payload.roundingTypes?.length
              ? `&roundingTypes=${payload.roundingTypes}`
              : ''
          }`,
          {}
        );
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'employee-round-history',
          id: payload.employeeId
        }
      ]
    }),
    /**
     * Export Endpoints
     */
    getPatientRoundsReportDownload: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetPatientRoundsReportDownloadPayload) => {
        const params = new URLSearchParams({
          ...payload,
          isExport: 'true'
        } as any).toString();
        return getData(`${getPatientRoundsReportUrl}?${params}`, {});
      },
      transformResponse: response => {
        const patientRoundsReportList =
          response?.map((round: any) => {
            const answersWithIndex =
              round.answers?.map((ans: any, index: any) => ({
                [`question${index + 1}`]: ans?.question?.replaceAll('"', ''),
                [`answer${index + 1}`]:
                  ans?.answer && ans.answer.length > 0
                    ? ans?.answer[0].value
                    : ''
              })) || [];
            const ansObject = answersWithIndex.reduce(
              (acc: any, value: any) => ({
                ...acc,
                ...value
              }),
              {}
            );
            return {
              unit: round?.unit || '',
              room: round?.room || '',
              patientId: round?.patientId || '',
              patient: `${round?.patientFirstName} ${round?.patientLastName}`,
              patientFirstName: round?.patientFirstName || '',
              patientLastName: round?.patientLastName || '',
              patientMRN: round?.patientMRN || '',
              createdUserName: `${round?.userFirstName} ${round?.userLastName}`,
              createdUserId: round?.userId || '',
              accountNumber: round?.accountNum || '',
              facilityId: round?.facilityId || '',
              status:
                round.status === 'rounded' ? 'Rounded' : 'Unable To Round',
              date:
                round?.roundDate && date.isOrbitTimestamp(round?.roundDate)
                  ? date.parseDate(round?.roundDate).toLocaleString([], {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit'
                    })
                  : '',
              time:
                round?.roundDate && date.isOrbitTimestamp(round?.roundDate)
                  ? date.parseDate(round?.roundDate).toLocaleString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : '',
              templateName: round?.templateName || '',
              ...ansObject
            };
          }) || [];
        return patientRoundsReportList;
      },
      transformErrorResponse: processError,
      providesTags: () => [
        {
          type: 'patient-rounds-report-export'
        }
      ],
      async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(
            setToastOptions({
              msg: 'Failed to retrieve report!',
              feedback: 'error',
              duration: 1200,
              speed: 350,
              question: true
            })
          );
          dispatch(slideIn());
        }
      }
    }),
    getEmployeeRoundsReportDownload: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetEmployeeRoundsReportDownloadPayload) => {
        const params = new URLSearchParams(payload as any).toString();
        return getData(`${getEmployeeRoundsReportUrl}?${params}`, {});
      },
      transformResponse: (response, _meta, arg) => {
        const mostRecentRoundCleanData =
          response
            ?.filter(
              (round: any) =>
                qualifyingEmpRoundingTemplates.includes(round.templateName) ||
                round?.complianceStatus === 'notrounded'
            )
            .reduce((acc: any[], round: any) => {
              return [
                ...acc,
                {
                  roundId: round?.roundId || '',
                  department: round?.employeeDept || 'Unknown',
                  employee:
                    `${round?.employeeFirstName} ${round?.employeeLastName}` ||
                    '',
                  employeeId: round?.employeeId || '',
                  employeeFirstName: round?.employeeFirstName || '',
                  employeeLastName: round?.employeeLastName || '',
                  employeeHireDate: employeeHireDateHelper(
                    round?.employeeHireDate
                  )
                    ? date.datePrettier(round?.employeeHireDate)
                    : 'Unknown',
                  roundedBy:
                    round?.userFirstName && round?.userLastName
                      ? `${round?.userFirstName} ${round?.userLastName}`
                      : '',
                  createdUserId: round?.userId || '',
                  facilityId: round?.facilityId || '',
                  date: round?.roundDate || '',
                  roundDate: round?.roundDate
                    ? date.dayOfYr(round?.roundDate)
                    : '',
                  status:
                    round?.complianceStatus === 'compliant'
                      ? 'Compliant'
                      : round?.complianceStatus === 'notrounded'
                      ? 'Not Rounded'
                      : 'Rounded',
                  templateName:
                    round?.templateName === 'Unknown'
                      ? ''
                      : round?.templateName || '',
                  answers: round?.answers || []
                }
              ];
            }, []) || [];

        // TODO: Front-end has to handle sorting until mobile no longer needs export endpoint
        if (arg.sortBy === 'employeeDept') {
          if (arg.sortOrder === 'asc') {
            mostRecentRoundCleanData.sort((a: any, b: any) =>
              a.department.localeCompare(b.department)
            );
          } else {
            mostRecentRoundCleanData.sort((a: any, b: any) =>
              b.department.localeCompare(a.department)
            );
          }
        } else if (arg.sortBy === 'employeeName') {
          if (arg.sortOrder === 'asc') {
            mostRecentRoundCleanData.sort((a: any, b: any) =>
              a.employee.localeCompare(b.employee)
            );
          } else {
            mostRecentRoundCleanData.sort((a: any, b: any) =>
              b.employee.localeCompare(a.employee)
            );
          }
        } else if (arg.sortBy === 'complianceStatus') {
          if (arg.sortOrder === 'asc') {
            mostRecentRoundCleanData.sort(() => 0);
          } else {
            mostRecentRoundCleanData.sort(() => 0);
          }
        } else if (arg.sortBy === 'roundedBy') {
          if (arg.sortOrder === 'asc') {
            mostRecentRoundCleanData.sort((a: any, b: any) =>
              a.roundedBy.localeCompare(b.roundedBy)
            );
          } else {
            mostRecentRoundCleanData.sort((a: any, b: any) =>
              b.roundedBy.localeCompare(a.roundedBy)
            );
          }
        } else if (arg.sortBy === 'templateName') {
          if (arg.sortOrder === 'asc') {
            mostRecentRoundCleanData.sort((a: any, b: any) =>
              a.templateName.localeCompare(b.templateName)
            );
          } else {
            mostRecentRoundCleanData.sort((a: any, b: any) =>
              b.templateName.localeCompare(a.templateName)
            );
          }
        } else if (arg.sortBy === 'roundDate') {
          if (arg.sortOrder === 'asc') {
            mostRecentRoundCleanData.sort((a: any, b: any) =>
              a.date.localeCompare(b.date)
            );
          } else {
            mostRecentRoundCleanData.sort((a: any, b: any) =>
              b.date.localeCompare(a.date)
            );
          }
        }

        if (arg.isDownload) {
          return mostRecentRoundCleanData;
        }

        const extrapolatedAnswersRoundsData = mostRecentRoundCleanData.map(
          (round: any) => {
            // This is mapping out the answers
            const answers =
              round.answers?.map((ans: any) => {
                return {
                  question: ans.question,
                  answer:
                    ans?.answer && ans.answer.length > 0
                      ? ans?.answer[0].value
                      : ''
                };
              }) || [];

            const answersWithIndex = answers.map((obj: any, index: number) => {
              return {
                [`question${index + 1}`]: obj?.question?.replaceAll('"', ''),
                [`answer${index + 1}`]: obj?.answer
              };
            });
            const ansObject = answersWithIndex.reduce(
              (acc: any, value: any) => {
                return {
                  ...acc,
                  ...value
                };
              },
              {}
            );

            return {
              ...round,
              type: round.templateName,
              ...ansObject
            };
          }
        );

        return extrapolatedAnswersRoundsData;
      },
      transformErrorResponse: processError,
      providesTags: () => [
        {
          type: 'employee-rounds-report-export'
        }
      ],
      async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(
            setToastOptions({
              msg: 'Failed to retrieve report!',
              feedback: 'error',
              duration: 1200,
              speed: 350,
              question: true
            })
          );
          dispatch(slideIn());
        }
      }
    }),
    getCensusReportDownload: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetCensusReportDownloadPayload) => {
        const params = new URLSearchParams(payload as any).toString();
        return getData(`${getCensusReportUrl}?${params}`, {});
      },
      transformResponse: response => {
        const censusReportList =
          response?.map((census: any) => {
            return {
              censusDate: date.formatDateDashtoSlash(census?.reportDate),
              unit: census?.unitName,
              unitId: census?.unitId,
              room: census?.room || '',
              patient: `${census?.firstName} ${census?.lastName}` || '',
              mrn: census?.mrn || '',
              roundDate: census.rounds[0]?.roundDate || '',
              roundedBy:
                census?.status === 'none'
                  ? ''
                  : census?.rounds.length > 0
                  ? `${census.rounds[0]?.userFirstName} ${census.rounds[0]?.userLastName}`
                  : '',
              status:
                census?.status === 'none'
                  ? 'Not Rounded'
                  : census?.status === 'rounded'
                  ? 'Rounded'
                  : 'Unable To Round'
            };
          }) || [];
        return censusReportList;
      },
      transformErrorResponse: processError,
      providesTags: () => [
        {
          type: 'patient-census-report-export'
        }
      ],
      async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(
            setToastOptions({
              msg: 'Failed to retrieve report!',
              feedback: 'error',
              duration: 1200,
              speed: 350,
              question: true
            })
          );
          dispatch(slideIn());
        }
      }
    }),
    getFrequencyReportDownload: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetEmployeeFreqReportDownloadPayload) => {
        const params = new URLSearchParams(payload as any).toString();
        return getData(
          `${getEmployeeRoundsReportUrl}?${params}&isFrequencyReport=true`,
          {}
        );
      },
      transformResponse: (response, _meta, arg) => {
        const collapsedFreqReportWithAnswersExtrapolated =
          response?.reduce((acc: any[], round: any) => {
            const foundEmpIndex = acc.findIndex(
              r =>
                `${r?.employeeId}:${r?.department || 'Unknown'}` ===
                `${round?.employeeId}:${round?.employeeDept || 'Unknown'}`
            );
            const answers =
              round.answers?.map((ans: any) => {
                return {
                  question: ans.question,
                  answer:
                    ans?.answer && ans.answer.length > 0
                      ? ans?.answer[0].value
                      : ''
                };
              }) || [];
            const answersWithIndex = answers.map((obj: any, index: any) => {
              return {
                [`question${index + 1}`]: obj?.question?.replaceAll('"', ''),
                [`answer${index + 1}`]: obj?.answer
              };
            });
            const ansObject = answersWithIndex.reduce(
              (ans: any, value: any) => {
                return {
                  ...ans,
                  ...value
                };
              },
              {}
            );
            const newRound = {
              department: round?.employeeDept || 'Unknown',
              employee:
                `${round?.employeeFirstName} ${round?.employeeLastName}` || '',
              employeeId: round?.employeeId || '',
              employeeFirstName: round?.employeeFirstName || '',
              employeeLastName: round?.employeeLastName || '',
              roundedBy: `${round?.userFirstName} ${round?.userLastName}`,
              createdUserId: round?.userId || '',
              date: round?.roundDate || '',
              type: round?.templateName || '',
              ...ansObject
            };
            if (foundEmpIndex === -1) {
              return [
                ...acc,
                {
                  department: round?.employeeDept || 'Unknown',
                  employeeId: round?.employeeId || '',
                  employee:
                    `${round?.employeeFirstName} ${round?.employeeLastName}` ||
                    '',
                  employeeFirstName: round?.employeeFirstName || '',
                  employeeLastName: round?.employeeLastName || '',
                  count: 1,
                  rounds: [newRound]
                }
              ];
            }
            acc[foundEmpIndex].count += 1;
            acc[foundEmpIndex].rounds.push(newRound);
            return [...acc];
          }, []) || [];

        // TODO: Front-end has to handle sorting until mobile no longer needs export endpoint
        if (arg.sortBy === 'employeeDept') {
          if (arg.sortOrder === 'asc') {
            collapsedFreqReportWithAnswersExtrapolated.sort((a: any, b: any) =>
              a.department.localeCompare(b.department)
            );
          } else {
            collapsedFreqReportWithAnswersExtrapolated.sort((a: any, b: any) =>
              b.department.localeCompare(a.department)
            );
          }
        } else if (arg.sortBy === 'employeeName') {
          if (arg.sortOrder === 'asc') {
            collapsedFreqReportWithAnswersExtrapolated.sort((a: any, b: any) =>
              a.employee.localeCompare(b.employee)
            );
          } else {
            collapsedFreqReportWithAnswersExtrapolated.sort((a: any, b: any) =>
              b.employee.localeCompare(a.employee)
            );
          }
        } else if (arg.sortBy === 'total') {
          if (arg.sortOrder === 'asc') {
            collapsedFreqReportWithAnswersExtrapolated.sort(
              (a: any, b: any) => a.count - b.count
            );
          } else {
            collapsedFreqReportWithAnswersExtrapolated.sort(
              (a: any, b: any) => b.count - a.count
            );
          }
        }

        return collapsedFreqReportWithAnswersExtrapolated;
      },
      transformErrorResponse: processError,
      providesTags: () => [
        {
          type: 'employee-frequency-report-export'
        }
      ],
      async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(
            setToastOptions({
              msg: 'Failed to retrieve report!',
              feedback: 'error',
              duration: 1200,
              speed: 350,
              question: true
            })
          );
          dispatch(slideIn());
        }
      }
    })
  })
});

export const {
  useGetPatientComplianceReportQuery,
  useLazyGetPatientComplianceReportQuery,
  useGetPatientRoundsReportQuery,
  useLazyGetPatientRoundsReportQuery,
  useGetCensusReportQuery,
  useLazyGetCensusReportQuery,
  useGetPatientRoundHistoryQuery,
  useLazyGetPatientRoundHistoryQuery,
  useGetEmployeeComplianceReportQuery,
  useGetSearchPatientRoundHistoryQuery,
  useLazyGetSearchPatientRoundHistoryQuery,
  useLazyGetEmployeeComplianceReportQuery,
  useGetEmployeeRoundsReportQuery,
  useLazyGetEmployeeRoundsReportQuery,
  useGetEmployeeFrequencyReportQuery,
  useLazyGetEmployeeFrequencyReportQuery,
  useGetEmployeeRoundHistoryQuery,
  useLazyGetEmployeeRoundHistoryQuery,
  useGetPatientRoundsReportDownloadQuery,
  useLazyGetPatientRoundsReportDownloadQuery,
  useGetEmployeeRoundsReportDownloadQuery,
  useLazyGetEmployeeRoundsReportDownloadQuery,
  useGetCensusReportDownloadQuery,
  useLazyGetCensusReportDownloadQuery,
  useGetFrequencyReportDownloadQuery,
  useLazyGetFrequencyReportDownloadQuery
} = reportsApi;
